import { CampaignStatus, StatusFilter, TreeNode } from "./clientDashboardEvents";

/** The selected campaign's node in the filter tree, or null when none is selected. */
export function findCampaign(tree: readonly TreeNode[], launchId: number | null): TreeNode | null {
    if (!launchId) return null;
    for (const engagement of tree) {
        for (const objective of engagement.children) {
            const campaign = objective.children.find((l) => l.id === launchId);
            if (campaign) return campaign;
        }
    }
    return null;
}

/**
 * Dates arrive as plain `YYYY-MM-DD`. They are parsed at an explicit local midnight because
 * `new Date("2026-08-18")` is read as UTC and renders as the 17th for anyone west of
 * Greenwich, which is how a flight ends up displayed a day out.
 */
function asDate(value?: string | null): Date | null {
    return value ? new Date(`${value}T00:00:00`) : null;
}

function today(): Date {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
}

/**
 * "Aug 18", and "Aug 18, 2025" once the year is not this one.
 *
 * The ticket's example omits the year, which is right for the campaign a CSM is reviewing
 * this afternoon and wrong the moment a flight crosses New Year: "Dec 20 to Feb 10" reads as
 * a backwards ten-month range, and two ended campaigns a year apart are indistinguishable in
 * a dropdown a client picks between.
 */
export function formatDate(value?: string | null): string | null {
    const date = asDate(value);
    if (!date) return null;
    const sameYear = date.getFullYear() === today().getFullYear();
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        ...(sameYear ? {} : { year: "numeric" }),
    });
}

/** "Aug 18 to Nov 13" — the AC4 wording. One-sided when only one bound is scheduled. */
export function formatFlight(campaign: TreeNode): string | null {
    const start = formatDate(campaign.start_date);
    const end = formatDate(campaign.end_date);
    if (start && end) return `${start} to ${end}`;
    if (start) return `Starts ${start}`;
    if (end) return `Ends ${end}`;
    return null;
}

/**
 * Whether an Upcoming campaign's scheduled start is still ahead of us.
 *
 * Upcoming does not mean "starts later": the server also calls a campaign Upcoming when
 * nothing has published yet, whatever its schedule says (see client_campaign_status.py). So
 * a campaign scheduled from June that nobody launched is Upcoming in October, and anything
 * phrased in the future tense around its start date would be telling a client it begins on a
 * day that has already passed.
 */
export function startsInFuture(campaign: TreeNode): boolean {
    const start = asDate(campaign.start_date);
    return start !== null && start > today();
}

/**
 * The selector label. An Upcoming campaign leads with when it begins rather than its flight,
 * so the option reads as a campaign that has not run instead of one with no numbers (AC3).
 */
export function campaignLabel(campaign: TreeNode): string {
    const detail =
        campaign.status === "UPCOMING"
            ? startsInFuture(campaign)
                ? `Starts ${formatDate(campaign.start_date)}`
                : "Not started"
            : formatFlight(campaign);
    return detail ? `${campaign.name} · ${detail}` : campaign.name;
}

/**
 * Days left in the flight, counting today, or null when there is no end date.
 *
 * Inclusive of both ends, which is what the rest of the platform counts: the end date is the
 * last day the campaign serves — `campaign_status` keeps a campaign Live through it — and
 * `budget_service.get_engagement_active_days_remaining` is documented as inclusive of its
 * `as_of`. Exclusive counting would put "0 days remaining" on a campaign that is still
 * running and disagree with the budget header by one all the way along.
 */
export function daysRemaining(campaign: TreeNode): number | null {
    const end = asDate(campaign.end_date);
    if (!end) return null;
    const days = Math.round((end.getTime() - today().getTime()) / 86400000) + 1;
    return days > 0 ? days : 0;
}

export const STATUS_LABEL: Record<CampaignStatus, string> = {
    UPCOMING: "Upcoming",
    LIVE: "Live",
    ENDED: "Ended",
};

/**
 * The filter tree with everything the selected status excludes removed: campaigns of another
 * status, then the objectives and engagements left with no campaign to offer.
 *
 * The dropdowns and the empty state both read this, so they cannot disagree — a dropdown
 * offering an ended campaign under Live is a zeroed dashboard one click away, which is what
 * PO review found. ALL returns the tree untouched, so the default view is the one it was.
 *
 * Narrowing happens here rather than on the server because the response is also what labels
 * an upcoming campaign in the All view and what CampaignDates reads the selected campaign's
 * flight from: the tree has to arrive complete, with a status on every campaign.
 */
export function treeForStatus(tree: readonly TreeNode[], status: StatusFilter): TreeNode[] {
    if (status === "ALL") return tree as TreeNode[];
    return tree
        .map((engagement) => ({
            ...engagement,
            children: engagement.children
                .map((objective) => ({
                    ...objective,
                    children: objective.children.filter((l) => l.status === status),
                }))
                .filter((objective) => objective.children.length > 0),
        }))
        .filter((engagement) => engagement.children.length > 0);
}
